import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";

import { IconChevronLeft } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useStyles } from "./style";

import { useForm } from "@mantine/form";
import {
  addNewCardSkypoint,
  createNewCardSkypoint,
  updateExistingCardSkypoint,
} from "../../services/payment";
import swal from "sweetalert";
import { CiCreditCard1 } from "react-icons/ci";

const PaymentInfo = () => {
  const [value] = useState("manual");

  const navigate = useNavigate();
  const { state } = useLocation();
  const { profile } = useSelector((state) => state.profile);

  const connected = state?.card;
  const mode = state?.mode || (connected ? "update" : "create-new");
  const isCreateNew = mode === "create-new";

  const { classes } = useStyles();

  const [connectingCard, setConnectingCard] = useState(false);

  const form = useForm({
    initialValues: {
      nameCard: "",
      cardNumber: "",
      cardExp: null,
      cardMonth: null,
      cardYear: null,
      CVV: "",
    },
    validate: {
      nameCard: (value) => (value ? null : "Please add card name"),

      cardNumber: (value) => {
        if (!isCreateNew && connected) return null;
        return value && value.length <= 16
          ? null
          : "Please add a valid card number";
      },

      cardMonth: (value) => {
        const isValidLength = value && value.length === 2;
        return isValidLength ? null : "Please add a valid month";
      },

      cardYear: (value) => {
        const isValidLength = value && value.length === 2;
        return isValidLength ? null : "Please add a valid year";
      },

      CVV: (value) => {
        if (!isCreateNew && connected) return null;
        const isValidLength =
          value && (value.length === 3 || value.length === 4);
        return isValidLength ? null : "Please add a valid number";
      },
    },
  });

  useEffect(() => {
    if (!connected || isCreateNew) return;

    const getLast4Masked = (number) => {
      if (!number) return "";
      return number.slice(-4);
    };

    form.setValues({
      nameCard: connected.cardName || "",
      cardNumber: getLast4Masked(connected.cardNumber) || "",
    });
  }, [connected]);

  useEffect(() => {
    if (form.getInputProps("cardNumber")?.value?.length > 16) {
      form.setFieldValue(
        "cardNumber",
        form.getInputProps("cardNumber")?.value?.slice(0, 16),
      );
    }
  }, [form.getInputProps("cardNumber")?.value]);

  useEffect(() => {
    if (form.getInputProps("CVV")?.value?.length > 4) {
      form.setFieldValue("CVV", form.getInputProps("CVV")?.value?.slice(0, 4));
    }
  }, [form.getInputProps("CVV")?.value]);

  useEffect(() => {
    if (form.getInputProps("cardMonth")?.value?.length > 2) {
      form.setFieldValue(
        "cardMonth",
        form.getInputProps("cardMonth")?.value?.slice(0, 2),
      );
    }
  }, [form.getInputProps("cardMonth")]);

  useEffect(() => {
    if (form.getInputProps("cardYear")?.value?.length > 2) {
      form.setFieldValue(
        "cardYear",
        form.getInputProps("cardYear")?.value?.slice(0, 2),
      );
    }
  }, [form.getInputProps("cardYear")]);

  const onFinish = async (values) => {
    const { cardMonth, cardYear } = values;
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;

    const expMonth = Number(cardMonth);
    const expYear = Number(cardYear);

    if (
      expMonth < 1 ||
      expMonth > 12 ||
      expYear < currentYear ||
      (expYear === currentYear && expMonth < currentMonth)
    ) {
      swal({
        title: "Invalid Card Expiry",
        text: "Please enter a valid card expiration month and year.",
        icon: "error",
        dangerMode: true,
      });
      return;
    }

    let obj = {
      ...values,
      cardExp: `${cardMonth}/${cardYear}`,
      userId: profile?.user.id,
    };

    if (isCreateNew) {
      obj.email = profile?.user?.email;
    }

    delete obj?.cardMonth;
    delete obj?.cardYear;

    const confirmText = isCreateNew
      ? "A new card will be added to your account."
      : "This action cannot be undone. Previous added credit card will be replaced with this new card and all upcoming payments will be processed on this new card.";

    const willProceed = await swal({
      title: `Are you sure?`,
      text: confirmText,
      icon: "warning",
      dangerMode: !isCreateNew,
      buttons: ["Cancel", "Proceed"],
    });

    if (willProceed) {
      setConnectingCard(true);

      if (isCreateNew) {
        createNewCardSkypoint(obj, (error) => {
          setConnectingCard(false);
          if (!error) navigate("/home/settings");
        });
      } else if (connected) {
        updateExistingCardSkypoint(obj, (error) => {
          setConnectingCard(false);
          if (!error) navigate("/home/settings");
        });
      } else {
        addNewCardSkypoint(obj, (error) => {
          setConnectingCard(false);
          if (!error) navigate("/home/settings");
        });
      }
    } else {
      navigate("/home/settings");
    }
  };

  const onBack = () => {
    navigate("/home/settings");
  };

  return (
    <Flex className={classes.paymentPageBox}>
      <Box className={classes.paymentPageBoxLeft}>
        <UnstyledButton
          onClick={onBack}
          className={classes.paymentPageBoxBackBtn}
        >
          <IconChevronLeft />
          Back
        </UnstyledButton>
        <img
          src="/assets/images/skypoint-logo2.png"
          alt=""
          className={classes.paymentPageBoxLogo}
        />
      </Box>
      <Box className={classes.paymentPageBoxRight}>
        {value === "manual" && (
          <form
            className={classes.form}
            onSubmit={form.onSubmit((values) => onFinish(values))}
          >
            <h4>
              <CiCreditCard1 size={34} />
              {isCreateNew ? "Add New Card" : "Credit or Debit Card"}
            </h4>
            <Box className={classes.inputs}>
              <TextInput
                type=""
                label="Name on Card"
                placeholder="Eg.abc"
                {...form.getInputProps("nameCard")}
                className={classes.inputFieldStyle}
              />
            </Box>

            <Box className={classes.inputs}>
              <TextInput
                type="number"
                label="Card Number"
                className={classes.inputFieldStyle}
                placeholder="Eg.5952 **** **** ****"
                disabled={!isCreateNew && !!connected}
                {...form.getInputProps("cardNumber")}
              />
              {form.errors.cardNumber && (
                <Box style={{ color: "red" }}>{form.errors.cardNumber}</Box>
              )}
            </Box>
            <Grid align="flex-end" gutter={11}>
              <Grid.Col span={4}>
                <TextInput
                  type="number"
                  label="Month"
                  placeholder="MM"
                  className={classes.inputFieldStyle}
                  {...form.getInputProps("cardMonth")}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  type="number"
                  label="Year"
                  placeholder="YY"
                  className={classes.inputFieldStyle}
                  {...form.getInputProps("cardYear")}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  label="CVV"
                  type="number"
                  variant="filled"
                  placeholder="Eg.***"
                  disabled={!isCreateNew && !!connected}
                  className={classes.inputFieldStyle}
                  {...form.getInputProps("CVV")}
                />
              </Grid.Col>
            </Grid>
            <Button
              type="submit"
              loading={connectingCard}
              className={classes.payBtn}
            >
              Connect
            </Button>
            <p className={classes.agreeTermsCondText}>
              By subscribing, you agree to our{" "}
              <Link to="/term-conditions">terms and conditions</Link>.
            </p>
          </form>
        )}
      </Box>
    </Flex>
  );
};

export default PaymentInfo;
